// Spec 45 component-behavior — link variant of cmp_ds_button isolated
// as a first-class Link component. The button-link variant + the Link
// component diverge on default: button-link is for in-flow ACTIONS
// rendered as a link affordance; Link is for actual NAVIGATION.
//
// Two key rules per spec 43 + spec 45:
//   * "Never rely on color alone to indicate link" — underline is the
//     PRIMARY affordance. Variants render with persistent underline OR
//     reveal-on-hover (subtle); never bare-text-only-color.
//   * External links (target="_blank") get rel="noopener noreferrer"
//     automatically — security + privacy default.
//   * Underline strategy: 'persistent' (always) or 'hover' (only on
//     :hover/:focus). 'persistent' is the WCAG-safest default; 'hover'
//     is for inline-text contexts where every word would otherwise be
//     underlined.

import { forwardRef, type AnchorHTMLAttributes, type ReactNode } from "react";

import { cx } from "../lib/cx.ts";

export type LinkUnderline = "persistent" | "hover";

export interface LinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  readonly underline?: LinkUnderline;
  /**
   * `true` opens the link in a new tab AND sets rel="noopener noreferrer"
   * (security + privacy). For internal navigation, prefer your router's
   * <Link> instead of this component's external-tab affordance.
   */
  readonly external?: boolean;
  readonly children?: ReactNode;
}

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(
  (
    {
      underline = "persistent",
      external = false,
      target,
      rel,
      className,
      children,
      ...rest
    },
    ref,
  ) => {
    const effectiveTarget = external ? "_blank" : target;
    // Always layer noopener+noreferrer when target=_blank — both
    // protect against tabnabbing + Referer leakage.
    const effectiveRel = effectiveTarget === "_blank"
      ? mergeRel(rel, "noopener noreferrer")
      : rel;

    return (
      <a
        ref={ref}
        data-component="link"
        data-underline={underline}
        target={effectiveTarget}
        rel={effectiveRel}
        className={cx("nai-link", className)}
        {...rest}
      >
        {children}
      </a>
    );
  },
);

Link.displayName = "Link";

function mergeRel(existing: string | undefined, required: string): string {
  if (existing === undefined || existing.length === 0) return required;
  const tokens = new Set(existing.split(/\s+/).filter((t) => t.length > 0));
  for (const t of required.split(/\s+/)) tokens.add(t);
  return Array.from(tokens).join(" ");
}
